import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useMemo } from 'react';

import * as yup from 'yup';
import { BooleanRendererYesNo } from '../../../../components/AgTable/cell-renderers/BooleanRenderer';
import { EditableBooleanFieldRenderer } from '../../../../components/AgTable/cell-renderers/EditableBooleanFieldRenderer';
import { StringListCellRenderer } from '../../../../components/AgTable/cell-renderers/StringListCellRenderer';
import { RendererType } from '../../../../data-models/field-definition';
import { field2ToField3, GridRef } from '../../../../data-models/field2.data-model';
import { field3ToColumnDef } from '../../../../data-models/field3.data-model';
import { secondaryRespondentsField } from '../../../../schemas/Company.schema';
import { customFieldsListAtom } from '../../../../services/state/AdminPanel/CustomFieldsStateJ';
import { clientConfigFieldsAtom } from '../../../../services/state/AppConfigStateJ';
import { kpiActiveTemplatesListStateJ } from '../../../../services/state/KPI/KPITemplatesStateJ';
import { useRespondentApprovalStatuses } from '../../../../services/queries-hooks/RespondentApprovalStatusQueriesHooks';
import { withOverrides } from '../../../../util/ag-grid-utils';
import { MaggieFeatureFlags } from '../../../../util/feature-flags';
import { mergeRespondentDisplay } from '../../../../util/respondentStatus';
import { useSchemaToGrid } from '../../../../util/schema-utils';
import { MONTHS } from '../Sections/FormItem/KPITable/useKPITableMetricsInputs';
import { IKpiCompanyData } from './AllCompaniesData';
import { AllCompaniesMenu } from './components/cellRenderers/AllCompaniesMenu';
import { AssignmentStatusRenderer } from './components/cellRenderers/AssignmentStatusRenderer';
import { RequiredDataRenderer } from './components/cellRenderers/RequiredDataRenderer';
import { RespondentStatusCellRenderer } from './components/cellRenderers/RespondentStatusCellRenderer';
import { useRespondentApprovalsEnabled } from '../../hooks/useRespondentApprovalsEnabled';
import {
  resolveTemplateDisplayName,
  TemplateNamesCellRenderer,
} from './components/cellRenderers/TemplateNamesCellRenderer';

export function allKpiCompaniesFields() {
  return {
    reporting: yup.boolean().optional().label('Reporting'),
    id: yup.number().label('Company'),
    sectorId: yup.number().label('Sector'),
    assigned: yup.boolean(),
    templateUuids: yup.array().of(yup.string()).optional().label('Templates'),
    fye: yup.number().optional().label('FYE'),
    respondents: yup.array().of(yup.string()).optional(),
    secondaryRespondents: secondaryRespondentsField(),
    domain: yup.string().nullable().optional().label('Domain'),
  };
}

export function allKpiCompaniesGridSchema() {
  const fields = allKpiCompaniesFields();

  return yup.object().shape({
    ...fields,
    id: fields.id.gridMeta({
      renderer: RendererType.companyId,
      enableRowGroup: true,
    }),
    sectorId: fields.sectorId.gridMeta({
      renderer: RendererType.id,
      formatter: 'sector',
      enableRowGroup: true,
    }),
    assigned: fields.assigned.gridMeta({
      enableRowGroup: true,
    }),
    fye: fields.fye.gridMeta({
      renderer: RendererType.text,
    }),
    domain: fields.domain.gridMeta({
      renderer: RendererType.text,
    }),
  });
}

export function useAllCompaniesColDefs() {
  const schemaToColDefs = useSchemaToGrid();
  const customFields = useAtomValue(customFieldsListAtom);
  const activeTemplates = useAtomValue(kpiActiveTemplatesListStateJ);
  const isConsolidated =
    (useAtomValue(clientConfigFieldsAtom)?.Using_Consolidated_Data_Collection as boolean) ?? false;
  const { useStarbreakerCollectionV2 } = useFlags<MaggieFeatureFlags>();
  const isConsolidatedV2 = isConsolidated && useStarbreakerCollectionV2;
  const showRespondentStatuses = useRespondentApprovalsEnabled();
  const { data: respondentStatusLookup } = useRespondentApprovalStatuses(showRespondentStatuses);

  const activeTemplateUuids = useMemo(() => new Set(activeTemplates?.map((t) => t.uuid)), [activeTemplates]);

  const overrideDefs: Partial<Record<keyof IKpiCompanyData, ColDef<IKpiCompanyData>>> = useMemo(
    () => ({
      id: {
        initialSort: 'asc',
      },
      reporting: {
        cellRenderer: isConsolidated ? EditableBooleanFieldRenderer : BooleanRendererYesNo,
        ...(isConsolidated && {
          enableRowGroup: false,
          aggFunc: (params) => params.values.length > 0 && params.values.every((v) => v === true),
        }),
      },
      respondents: {
        filter: 'agSetColumnFilter',
        enableRowGroup: false,
        cellRenderer: RequiredDataRenderer,
        ...(showRespondentStatuses
          ? {
              // Return the visible set so filter / group / "Missing Data" / export match the cell.
              valueGetter: (params: ValueGetterParams<IKpiCompanyData>) =>
                mergeRespondentDisplay(
                  params.data?.respondents,
                  respondentStatusLookup?.get(params.data?.id ?? -1)
                ).visible,
              cellRendererParams: {
                valueRenderer: RespondentStatusCellRenderer,
                title: 'Respondents',
                statusLookup: respondentStatusLookup,
              },
            }
          : {
              cellRendererParams: {
                valueRenderer: StringListCellRenderer,
                title: 'Respondents',
              },
            }),
      },
      secondaryRespondents: {
        filter: 'agSetColumnFilter',
        enableRowGroup: false,
        cellRenderer: StringListCellRenderer,
        cellRendererParams: {
          title: 'CC Email',
        },
      },
      assigned: {
        cellRenderer: AssignmentStatusRenderer,
      },
      templateUuids: {
        headerName: 'Templates',
        enableRowGroup: false,
        cellRenderer: TemplateNamesCellRenderer,
        cellRendererParams: { activeTemplateUuids },
        valueFormatter: (params) => {
          if (!Array.isArray(params.value)) {
            return params.value ?? '';
          }
          const uuids: string[] = params.value;
          if (uuids.length === 0) {
            return '';
          }
          return uuids.map((uuid) => resolveTemplateDisplayName(uuid, activeTemplateUuids)).join(', ');
        },
        filterValueGetter: (params) => {
          const uuids: string[] = params.data?.templateUuids ?? [];
          if (uuids.length === 0) {
            return '';
          }
          return uuids.map((uuid) => resolveTemplateDisplayName(uuid, activeTemplateUuids));
        },
      },
      fye: {
        valueGetter: (params: ValueGetterParams) => {
          return params.data?.fye ? MONTHS[params.data?.fye - 1] : null;
        },
        valueFormatter: ({ value }) => value,
        cellRenderer: RequiredDataRenderer,
      },
      domain: {
        cellRenderer: RequiredDataRenderer,
      },
    }),
    [activeTemplateUuids, isConsolidated, showRespondentStatuses, respondentStatusLookup]
  );

  return useCallback(() => {
    const columnDefs = schemaToColDefs(allKpiCompaniesGridSchema(), [
      'id',
      ...(isConsolidatedV2 ? (['domain'] as const) : []),
      'sectorId',
      'reporting',
      isConsolidated ? 'templateUuids' : 'assigned',
      'fye',
      'respondents',
      ...(isConsolidated ? [] : (['secondaryRespondents'] as const)),
    ]);

    const customFieldColDefs = (customFields ?? []).reduce((acc, field) => {
      if (field.systemMeta?.gridRefs?.includes(GridRef.kpiDataCollection)) {
        acc.push(field3ToColumnDef(field2ToField3(field)));
      }
      return acc;
    }, [] as ColDef[]);

    return withOverrides(columnDefs, overrideDefs)
      .concat({
        headerName: '',
        maxWidth: 70,
        flex: 0,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        cellRenderer: AllCompaniesMenu,
        pinned: 'right',
      })
      .concat(customFieldColDefs);
  }, [customFields, isConsolidated, isConsolidatedV2, overrideDefs, schemaToColDefs]);
}

export const defaultColDef = {
  flex: 1,
  resizable: true,
  sortable: true,
  filter: 'agSetColumnFilter',
  enableRowGroup: true,
  minWidth: 150,
  getQuickFilterText: () => {
    return '';
  },
};
